/**
 * "Dá para prometer teste grátis AQUI?" — a régua única das portas do PRO.
 *
 * Estudo de divulgação do PRO (`docs/product/estudo-pro-divulgacao.md`, 26/09):
 * de ~25 portas para a `/pro`, só a folha pós-onboarding e a própria `/pro`
 * diziam que dá para começar de graça. Cada porta passa a pedir a frase a este
 * módulo — ninguém escreve "grátis" à mão, porque "grátis" que o checkout nega
 * é a pior promessa possível (skill `billing-and-pro`, item 16).
 *
 * As regras são as MESMAS da `/pro`, não uma segunda leitura delas:
 *  · web/PWA → `webPrices()` (o carimbo `proTrialConsumedAt` cala o teste, o
 *    override do painel `/admin` troca os dias) — o mesmo cálculo da vitrine;
 *  · loja (Capacitor) → só o que a LOJA respondeu nesta sessão. A elegibilidade
 *    é por produto e exige consultar a loja; nenhuma porta dispara essa
 *    consulta. Quem a faz é a vitrine (`useNativeStorefront`), que grava o
 *    resultado aqui. Antes disso a porta não promete nada — calar é seguro,
 *    prometer e a loja cobrar na hora não é.
 */
import { trialInviteDays, webPrices, type ProPrices } from "./commerce/proPrices";

// ── Memória da resposta da loja (por processo, nunca persistida) ─────────────

/** `undefined` = a loja ainda não respondeu nesta sessão; `null` = respondeu "sem teste". */
let nativeTrialDays: number | null | undefined = undefined;
const listeners = new Set<() => void>();

/** Chamado pela vitrine nativa quando a loja devolve preços + elegibilidade. */
export function rememberNativeTrial(prices: Pick<ProPrices, "trialDays" | "annualTrialDays">): void {
  const next = trialInviteDays(prices);
  if (next === nativeTrialDays) return;
  nativeTrialDays = next;
  listeners.forEach((fn) => fn());
}

export function readNativeTrial(): number | null | undefined {
  return nativeTrialDays;
}

export function subscribeNativeTrial(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Só para teste. */
export function resetNativeTrialMemory(): void {
  nativeTrialDays = undefined;
  listeners.clear();
}

// ── A decisão ────────────────────────────────────────────────────────────────

export interface TrialOfferInput {
  /** `isPurchaseAvailable()` — sem compra nesta plataforma, não há convite. */
  purchaseAvailable: boolean;
  /** Build nativo (loja) × web/PWA. */
  native: boolean;
  /** Perfil carregado de uma conta real (não anônima). */
  signedIn: boolean;
  /** `effectiveIsPro` — PRO, cortesia e trial em curso já têm o PRO. */
  isPro: boolean;
  /** `users.proTrialConsumedAt` presente (web). */
  trialConsumed: boolean;
  /** Dias do override do painel `/admin` (`useProShowcaseOverlay().trialDays`). */
  trialDaysOverride: number | null;
  /** O que a loja respondeu nesta sessão (`readNativeTrial()`). */
  nativeTrialDays: number | null | undefined;
}

/** Dias de teste que a porta pode anunciar, ou `null` para não prometer. */
export function resolveTrialOffer(input: TrialOfferInput): number | null {
  if (!input.purchaseAvailable || !input.signedIn || input.isPro) return null;
  if (input.native) return input.nativeTrialDays ?? null;
  return trialInviteDays(webPrices(0, null, input.trialDaysOverride, input.trialConsumed));
}

/**
 * Rótulo do botão de uma porta: com teste, "Experimentar N dias grátis"; sem,
 * o rótulo que a porta já usava. Um lugar só para a frase — trocar o verbo
 * depois é uma linha.
 */
export function trialCtaLabel(days: number | null, fallback: string): string {
  return days ? `Experimentar ${days} dias grátis` : fallback;
}

/** "7 dias grátis" — para legendas curtas (rail da Lura, faixas). */
export function trialShortLabel(days: number | null): string | null {
  return days ? `${days} dias grátis` : null;
}
